import React, { useState } from "react";
import styled from "styled-components";
import fontSize from "../../../theme/fonts/FontTheme";
import Image from "../../../assets/images/image/clothes/product-1.png";
import TextField from "../../../components/input/TextField";
import PrimaryButton from "../../../components/buttons/PrimaryButton";
import TextArea from "../../../components/input/TextArea";
import Tabs from "./Tabs";

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  padding: 50px 40px;
  box-sizing: border-box;

  @media (max-width: 950px) {
    flex-direction: column-reverse;
  }
`;

const InfoWrapper = styled.div`
  width: 50%;

  img {
    width: 100%;
    height: auto;
    max-height: 500px;
    object-fit: contain;
  }

  @media (max-width: 950px) {
    width: 100%;
  }
`;


const Title = styled.h3`
  max-width: 250px;
  font-weight: 400;
  letter-spacing: 0.02rem;
  font-size: ${fontSize.m};
`;

const List = styled.ol`
  padding: 20px;
  max-width: 500px;
  letter-spacing: 0.02rem;
  li {
    padding: 2px 0;
    font-size: ${fontSize.s};
  }
`;

const FormWrapper = styled.div`
  width: 50%;
  min-height: 500px;
  box-sizing: border-box;

  @media (max-width: 950px) {
    width: 100%;
    min-height: auto;
  }
`;

const Header = styled.div`
  display: flex;
  gap: 16px;
  align-items: end;
  padding-bottom: 32px;
  border-bottom: 1px solid #efefef;
`;

const MeasurementUnit = styled.div`
  font-size: ${fontSize.xxs};
  display: flex;
  gap: 8px;
  flex-direction: column;
`;

const MeasurementWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FieldsWrapper = styled.div`
  margin-top: 32px;
  display: grid;
  gap: 16px;
  grid-template-columns: repeat(2, 1fr);

  @media (max-width: 400px) {
    grid-template-columns: 1fr;
  }
`;

const Center = styled.div`
  display: flex;
  justify-content: center;
`;

const MEASUREMENTS = [
  { key: "length", label: "body length" },
  { key: "chest", label: "chest" },
  { key: "waist", label: "waist" },
  { key: "hip", label: "hip" },
  { key: "shoulders", label: "shoulders" },
  { key: "sleeveLength", label: "sleeve length" },
  { key: "sleeveBottom", label: "sleeve bottom" },
  { key: "armHole", label: "arm hole" },
  { key: "halfBack", label: "half back" },
  { key: "width", label: "width" },
];

const MEASUREMENT_INFO = {
  length: {
    title: "How To Take The Body Length Measurement",
    steps: [
      "Start at the highest point of the shoulder near the neck",
      "Run the measuring tape straight down the front of the body",
      "Measure to the desired garment length",
      "Ensure the tape follows a straight vertical line",
    ],
    image: Image,
  },

  chest: {
    title: "How To Take The Chest Measurement",
    steps: [
      "Stand straight with arms relaxed at the sides",
      "Wrap the tape around the fullest part of the chest",
      "Ensure the tape passes under the arms",
      "Keep the tape level and parallel to the floor",
    ],
    image: Image,
  },

  waist: {
    title: "How To Take The Waist Measurement",
    steps: [
      "Locate the natural waistline",
      "Wrap the tape around the waist comfortably",
      "Do not pull the tape too tight",
      "Measure while breathing normally",
    ],
    image: Image,
  },

  hip: {
    title: "How To Take The Hip Measurement",
    steps: [
      "Stand with feet together",
      "Wrap the tape around the fullest part of the hips and seat",
      "Ensure the tape stays level around the body",
      "Keep the tape snug but not tight",
    ],
    image: Image,
  },

  shoulders: {
    title: "How To Take The Shoulder Measurement",
    steps: [
      "Wear a well-fitted shirt or jacket",
      "Measure from one shoulder point to the other across the back",
      "Keep the tape straight and horizontal",
      "Do not curve the tape around the neck",
    ],
    image: Image,
  },

  sleeveLength: {
    title: "How To Take The Sleeve Length Measurement",
    steps: [
      "Start measuring from the shoulder point",
      "Run the tape down the arm to the wrist",
      "Keep the arm slightly bent",
      "Measure to where the sleeve should end",
    ],
    image: Image,
  },

  sleeveBottom: {
    title: "How To Take The Sleeve Bottom Measurement",
    steps: [
      "Wrap the tape around the wrist or sleeve opening area",
      "Measure where the cuff or sleeve hem will sit",
      "Ensure the tape is snug but comfortable",
      "Allow room for ease of movement",
    ],
    image: Image,
  },

  armHole: {
    title: "How To Take The Arm Hole Measurement",
    steps: [
      "Wrap the tape around the armhole opening",
      "Start at the shoulder seam",
      "Pass under the arm and back to the start point",
      "Keep the tape close to the body without pulling tight",
    ],
    image: Image,
  },

  halfBack: {
    title: "How To Take The Half Back Measurement",
    steps: [
      "Locate the center back of the neck",
      "Measure horizontally to the shoulder point",
      "Keep the tape straight across the back",
      "Ensure the shoulders are relaxed",
    ],
    image: Image,
  },

  rightCuff: {
    title: "How To Take The Right Cuff Measurement",
    steps: [
      "Wrap the tape around the wrist",
      "Measure at the point where the cuff will sit",
      "Ensure the tape is snug but comfortable",
      "Leave enough room for easy movement",
    ],
    image: Image,
  },

  width: {
    title: "How To Take The Width Measurement",
    steps: [
      "Lay the garment flat on a smooth surface",
      "Measure straight across from one side to the other",
      "Ensure the fabric is not stretched",
      "Keep the tape flat and straight",
    ],
    image: Image,
  },
};

function MeasurementSection() {
  const [unit, setUnit] = useState("cm");
  const [activeMeasurement, setActiveMeasurement] = useState("shoulders");

  const info = MEASUREMENT_INFO[activeMeasurement];

  return (
    <Wrapper>
      <InfoWrapper>
        <Title>{info.title}</Title>

        <List>
          {info.steps.map((step, index) => (
            <li key={index}>{step}</li>
          ))}
        </List>

        <img src={info.image} alt={activeMeasurement} />
      </InfoWrapper>
      <FormWrapper>
        <Header>
          <TextField
            text="UNIQUE NAME"
            label="Give a name to this measurements profile:"
          />
          <MeasurementUnit>
            <span>Unit of measurement:</span>
            <Tabs
              tabs={["cm", "inches"]}
              active={unit === "cm" ? 0 : 1}
              onChange={(index) => setUnit(index === 0 ? "cm" : "inches")}
            />
          </MeasurementUnit>
        </Header>
        <MeasurementWrapper>
          <FieldsWrapper>
            {MEASUREMENTS.map((item) => (
              <TextField
                key={item.key}
                text={unit}
                label={item.label}
                onFocus={() => setActiveMeasurement(item.key)}
              />
            ))}
          </FieldsWrapper>

          <TextArea label={"additional note (optional)"} text={"note"} />
          <Center>
            <PrimaryButton title={"SAVE MEASUREMENTS"} width="min(100%, 50%)" />
          </Center>
        </MeasurementWrapper>
      </FormWrapper>
    </Wrapper>
  );
}

export default MeasurementSection;
